import { useState } from 'react';
import {
  buildPresets,
  formatDelivery,
  toDatetimeLocalValue,
} from '../lib/schedule';

type Props = {
  value: Date | null;
  onChange: (d: Date | null) => void;
  disabled?: boolean;
};

/**
 * "Deliver later" control. Closed, it's a small clock button (or a pill
 * showing the scheduled time). Open, it offers a few presets plus a
 * custom date/time. Reused by the chat composer and the letter composer.
 */
export function ScheduleControl({ value, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);

  function pick(d: Date) {
    onChange(d);
    setOpen(false);
  }

  return (
    <div className="schedule">
      {value ? (
        <button
          type="button"
          className="schedule-pill"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          title="Change delivery time"
        >
          🕊️ Arrives {formatDelivery(value)}
          <span
            className="schedule-clear"
            role="button"
            aria-label="Send now instead"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
              setOpen(false);
            }}
          >
            ✕
          </span>
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-ghost composer-attach-btn"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          aria-label="Deliver later"
          aria-expanded={open}
          title="Deliver later"
        >
          🕊️
        </button>
      )}

      {open && (
        <>
          <div className="schedule-scrim" onClick={() => setOpen(false)} />
          <div className="schedule-menu" role="dialog" aria-label="Deliver later">
            <p className="schedule-menu-title">Deliver later</p>
            {buildPresets().map((p) => (
              <button
                key={p.id}
                type="button"
                className="schedule-menu-item"
                onClick={() => pick(p.when)}
              >
                <span className="schedule-menu-icon" aria-hidden="true">{p.icon}</span>
                <span className="schedule-menu-label">{p.label}</span>
                <span className="schedule-menu-when">{formatDelivery(p.when)}</span>
              </button>
            ))}
            <label className="schedule-custom">
              <span className="schedule-menu-icon" aria-hidden="true">🕰️</span>
              <span className="schedule-menu-label">Pick a time</span>
              <input
                type="datetime-local"
                className="schedule-custom-input"
                min={toDatetimeLocalValue(new Date(Date.now() + 60_000))}
                defaultValue={toDatetimeLocalValue(value ?? new Date(Date.now() + 3_600_000))}
                onChange={(e) => {
                  const d = new Date(e.target.value);
                  if (!isNaN(d.getTime()) && d.getTime() > Date.now()) pick(d);
                }}
              />
            </label>
            {value && (
              <button
                type="button"
                className="schedule-menu-now"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                Send now instead
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
